import { useCallback, useEffect, useState } from 'react';

const DISMISS_KEY = 'cas_pwa_install_dismissed';

const isStandaloneMode = () =>
  window.matchMedia('(display-mode: standalone)').matches
  || window.matchMedia('(display-mode: fullscreen)').matches
  || window.navigator.standalone === true;

const isIos = () => {
  const ua = window.navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(isStandaloneMode);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === '1');
  const [isIosDevice] = useState(isIos);

  useEffect(() => {
    const onInstalled = () => {
      setDeferredPrompt(null);
      setIsStandalone(true);
    };

    const onBeforeInstall = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const onDisplayModeChange = () => setIsStandalone(isStandaloneMode());

    window.addEventListener('appinstalled', onInstalled);
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.matchMedia('(display-mode: standalone)').addEventListener('change', onDisplayModeChange);

    return () => {
      window.removeEventListener('appinstalled', onInstalled);
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.matchMedia('(display-mode: standalone)').removeEventListener('change', onDisplayModeChange);
    };
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return outcome === 'accepted';
  }, [deferredPrompt]);

  const canNativeInstall = Boolean(deferredPrompt) && !isStandalone;
  const showIosHint = isIosDevice && !isStandalone && !dismissed && !canNativeInstall;
  const showInstallBanner = !isStandalone && !dismissed && (canNativeInstall || showIosHint);

  return {
    canNativeInstall,
    showIosHint,
    showInstallBanner,
    isStandalone,
    install,
    dismiss,
  };
}
